import React from 'react';
import { Box, Typography, Button, Grid, Container } from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import heroImg from '../../../images/biometric_hero.png';

const Hero = () => (
  <Box sx={{
    minHeight: '90vh',
    bgcolor: '#ffffff',
    backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(16,185,129,0.06) 0%, transparent 50%), radial-gradient(circle at 10% 80%, rgba(6,182,212,0.05) 0%, transparent 50%)',
    display: 'flex', alignItems: 'center',
    position: 'relative', overflow: 'hidden',
    py: { xs: 10, md: 4 },
    borderBottom: '1px solid #f0fdf4',
  }}>
    {/* Subtle dot grid */}
    <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(16,185,129,0.1) 1px, transparent 1px)', backgroundSize: '28px 28px', pointerEvents: 'none', opacity: 0.7 }} />

    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
      <Grid container spacing={6} alignItems="center">
        <Grid item xs={12} md={6}>
          {/* Badge */}
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '40px', px: 2.5, py: 1, mb: 4 }}>
            <FingerprintIcon sx={{ color: '#059669', fontSize: 20 }} />
            <Typography sx={{ color: '#047857', fontSize: 13, fontWeight: 700, letterSpacing: 1, fontFamily: "'Montserrat', sans-serif", textTransform: 'uppercase' }}>Biometric Attendance & Payroll</Typography>
          </Box>

          <Typography variant="h1" sx={{ color: '#0f172a', fontWeight: 900, fontSize: { xs: '2.4rem', md: '3.6rem' }, lineHeight: 1.12, mb: 3, fontFamily: "'Montserrat', sans-serif" }}>
            Zero Errors{' '}
            <Box component="span" sx={{ color: '#059669' }}>Attendance.</Box>
            <br />
            One-Click{' '}
            <Box component="span" sx={{ color: '#0891b2' }}>Payroll.</Box>
          </Typography>

          <Typography sx={{ color: '#475569', fontSize: 17, mb: 5, lineHeight: 1.8, fontFamily: "'Montserrat', sans-serif", maxWidth: 480 }}>
            Connect any fingerprint scanner or face recognition device. Our system auto-marks attendance, calculates salary, generates payslips and files compliance reports — completely automatically.
          </Typography>

          {/* Stats pills */}
          <Grid container spacing={2} sx={{ mb: 5 }}>
            {[['100%', 'Attendance Accuracy', '#059669', '#f0fdf4'], ['0', 'Manual Errors', '#dc2626', '#fef2f2'], ['5 mins', 'Payroll Processing', '#2563eb', '#eff6ff']].map(([v, l, c, bg]) => (
              <Grid item xs={4} key={l}>
                <Box sx={{ bgcolor: bg, border: `1px solid ${c}20`, borderRadius: '16px', p: 2, textAlign: 'center' }}>
                  <Typography sx={{ color: c, fontWeight: 900, fontSize: '1.5rem', fontFamily: "'Montserrat', sans-serif", lineHeight: 1 }}>{v}</Typography>
                  <Typography sx={{ color: '#64748b', fontSize: 11, fontWeight: 600, fontFamily: "'Montserrat', sans-serif", mt: 0.5 }}>{l}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
            <Button variant="contained" endIcon={<ArrowForwardIcon />} size="large"
              sx={{ bgcolor: '#059669', color: '#fff', px: 5, py: 1.6, borderRadius: '14px', fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 15, boxShadow: '0 10px 30px rgba(5,150,105,0.25)', '&:hover': { bgcolor: '#047857', transform: 'translateY(-2px)' }, transition: 'all 0.3s' }}>
              Book Free Demo
            </Button>
            <Button variant="outlined" size="large"
              sx={{ borderColor: '#d1d5db', color: '#374151', bgcolor: '#fff', px: 5, py: 1.6, borderRadius: '14px', fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 15, '&:hover': { borderColor: '#059669', color: '#059669', bgcolor: '#f0fdf4' }, transition: 'all 0.3s' }}>
              View Features
            </Button>
          </Box>

          {/* Trust badges */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {['1000+ Businesses', 'Any Brand Device', 'Auto Compliance'].map(t => (
              <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: '#10b981', fontSize: 17 }} />
                <Typography sx={{ color: '#475569', fontSize: 13.5, fontWeight: 600, fontFamily: "'Montserrat', sans-serif" }}>{t}</Typography>
              </Box>
            ))}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ position: 'relative', px: { xs: 2, md: 0 } }}>
            {/* Blob decoration */}
            <Box sx={{ position: 'absolute', inset: '5% -5%', borderRadius: '40px', background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', zIndex: 0, transform: 'rotate(2deg)' }} />
            {/* Image with clean white frame */}
            <Box sx={{ position: 'relative', zIndex: 1, borderRadius: '20px', bgcolor: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 30px 60px rgba(0,0,0,0.07)', overflow: 'hidden', transition: 'transform 0.4s', '&:hover': { transform: 'translateY(-8px)' } }}>
              {/* Fake browser top bar */}
              <Box sx={{ bgcolor: '#f8fafc', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 0.8, borderBottom: '1px solid #e2e8f0' }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#ef4444' }} />
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#22c55e' }} />
              </Box>
              <img src={heroImg} alt="Biometric Payroll System" style={{ width: '100%', display: 'block' }} />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

export default Hero;
